import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import SearchIcon from '@mui/icons-material/Search';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import CircularProgress from '@mui/material/CircularProgress';
import ReactTable from '../../Table';
import TableSkeleton from '../../Table/TableSkeleton';
import Typography from '../../Typography';
import Button from '../../Button';
import LongMenu from '../../MuiComponents/MuiMenu';
import { AlertContext } from '../../SuccessOrFailedModal';
import {
  useMembers,
  useRemoveMember,
  useResendInvite,
  useCancelInvite,
  useUpdateMemberRoleAccess,
} from '../../../hooks/members';
import { useValidateLogin } from '../../../hooks/auth';
import {
  platformRoles,
  accessModes,
  getSameFamilyRoles,
} from '../../../utils/constants/platformRoles';
import Storage from '../../../utils/Storage';
import InviteTeammateModal from './InviteTeammateModal';

/**
 * ROAR-4969/ROAR-4970 (ROAR-4802 / P1.11, PR2+PR3) — the Members pane (AC1, AC2, AC3, AC7, AC8,
 * AC10, AC12, AC13, AC15).
 *
 * Rows are whatever `GET /team/members` returns; this pane does no client-side company/MSSP
 * scoping of its own (AC7 is satisfied server-side by P1.10's scope-resolver).
 *
 * `platform_role`/`access_mode`/`status` are this Story's new vocabulary — not previously
 * rendered anywhere in this codebase — so they're mapped through local label tables rather than
 * shown raw. An unrecognized value (a future role/status this pane hasn't been updated for) falls
 * back to the raw string rather than hiding the row.
 *
 * IMPORTANT — there is no `invited` status value anywhere in this system: `users.status`'s real
 * enum is `active|inactive|deleted|blocked` (confirmed against the User model's own validator), so
 * a pending invite's placeholder user row just reads `status: 'inactive'`, indistinguishable from
 * a genuinely deactivated member by status alone. `invite_id` (a backend follow-up to ROAR-4851,
 * unreleased when this was written — branch `feature/team-members-invite-id`) is the real signal:
 * the matching `invites` row's own id when one is pending, `null` otherwise. `getDisplayStatus`
 * below derives an "Invited" display status from `invite_id`, never from the raw `status` field —
 * do not reintroduce a `status === 'invited'` check anywhere in this file.
 *
 * Invite/Edit/Remove are gated on the caller being a write-mode customer_admin/mssp_admin —
 * mirrors the backend's own requireInvitePermission.teamInvite / memberAuthorization.js check, so
 * a caller who couldn't actually perform the action never sees a control that would just 403.
 * Self (AC3) is identified by user id, matching the backend's own check
 * (memberAuthorization.js:resolveMutationTarget compares by id, not email) — this is a UI-only
 * convenience; the backend enforces it regardless.
 *
 * `hasTargetCompany` gates Edit/Remove/Cancel/Re-Invite too, not just Invite (code-review finding,
 * developer-confirmed during /build): PATCH/DELETE /team/members/:id and the invite resend/cancel
 * routes are never exempted from requiring selected_company_uuid (resolveCompanyScope.js /
 * inviteAuthorization.js), even for an mssp_admin acting on their own MSSP colleague (a request
 * the header is otherwise irrelevant to) — so without a company selected these would 403
 * unconditionally. Requiring a selection first avoids that dead-end.
 *
 * Cancel Invite / Re-Invite call the same already-shipped endpoints (ROAR-4853) using a row's
 * `invite_id`, shown only when it's non-null (i.e. only on a currently-pending invite). Known
 * backend gap, not fixed here (flagged by the backend author): cancelling only flips
 * `invites.status`, never `users.status`, so a cancelled row doesn't disappear from this list per
 * AC13's letter — it just loses its `invite_id` on the next refresh and reads as a plain
 * `inactive` member instead of vanishing.
 *
 * Visual redesign (developer-provided mock) deliberately does NOT include a CREDIT USED column —
 * the Story's own "Out of scope" section says that column "will not ship": per-user credit
 * attribution doesn't exist in the backend and can't be added cheaply.
 *
 * ROLE/ACCESS are live dropdowns (MUI Select), not a separate Edit modal (developer-confirmed,
 * reversing an earlier decision once seen live). Picking a new value confirms first (developer-
 * confirmed) via the same AlertContext popup every other destructive-ish action here uses — only
 * on confirm does `onUpdateField` call `useUpdateMemberRoleAccess`; a verbatim error popup on
 * failure (e.g. the last-write-admin guard, AC2) since there's no other way to surface why the
 * change didn't take. The Select stays fully controlled by server data (`value` is the row's own
 * `platform_role`/`access_mode`), so a cancelled confirm needs no revert — nothing was ever
 * locally changed. Role options are constrained to the target's own tier family
 * (`getSameFamilyRoles`) — editing never moves someone between tiers. Shown for every manageable
 * row, including a pending invite's (role/access are written at invite-creation per the Story's
 * own Scope section, so there's a real value to edit even before acceptance) — only
 * the caller's own row falls back to a plain, non-interactive pill (AC3).
 *
 * Search (`searchKey`) is server-side, same param `GET /team/members` already accepts. FILTER
 * (role/status) is client-side over whatever pages have already loaded — the endpoint has no
 * role/status query param, so filtering narrower than the loaded set can't reach rows further
 * down an unloaded page; scrolling to load more still works independently of an active filter.
 */
const ROLE_LABELS = {
  super_admin: 'Super Admin',
  mssp_admin: 'MSSP Admin',
  mssp_user: 'MSSP User',
  customer_admin: 'Company Admin',
  customer_user: 'Company User',
};

// "Read-Only" is confirmed as a UI label only in siemba-billing-db-design.html (the DB column
// itself never holds anything but write|read) — "Full Access" for write isn't equally confirmed
// there, but matches the design mock's own consistent usage for this column.
const ACCESS_LABELS = {
  write: 'Full Access',
  read: 'Read-Only',
};

const STATUS_LABELS = {
  active: 'Active',
  inactive: 'Inactive',
  blocked: 'Blocked',
  invited: 'Invited',
};

// Mirrors the backend's response vocabulary exactly (src/api/queries/members.js) — used to drive
// both the STATUS filter checkboxes and the pill color below.
const STATUS_PILL_CLASSES = {
  active: 'bg-primary-50 text-primary-700',
  invited: 'bg-functional-blue-50 text-functional-blue-600',
  inactive: 'bg-gray-100 text-[rgba(0,0,0,0.6)]',
  blocked: 'bg-functional-red-900 text-functional-red-700',
};

const resolveLabel = (labels, value) => {
  if (value === null || value === undefined || value === '') return '-';
  return labels[value] || value;
};

// The authoritative "is this row a pending invite" signal — see the module comment's IMPORTANT
// note: `status` never actually holds `'invited'`, only `invite_id` tells the truth.
const getDisplayStatus = (member) => (member?.invite_id != null ? 'invited' : member?.status);

const MANAGE_ROLES = [platformRoles.CUSTOMER_ADMIN, platformRoles.MSSP_ADMIN];
const STATUS_FILTER_OPTIONS = Object.keys(STATUS_LABELS);
const EMPTY_FILTERS = { role: [], status: [] };

const StatusPill = ({ status }) => (
  <span
    className={`inline-block rounded-full px-4 py-1.5 text-sm font-medium whitespace-nowrap ${
      STATUS_PILL_CLASSES[status] || 'bg-gray-100 text-[rgba(0,0,0,0.6)]'
    }`}
  >
    {resolveLabel(STATUS_LABELS, status)}
  </span>
);

// Fallback for a row the caller can't edit (their own, per AC3) — a plain, non-interactive pill.
const TextPill = ({ children }) => (
  <span className="inline-block rounded-full border border-grey2 px-4 py-1.5 text-sm whitespace-nowrap">
    {children}
  </span>
);

// Live, pill-styled dropdown for ROLE/ACCESS — same rounded-pill look as TextPill, but an actual
// MUI Select. Picking a new value doesn't call the mutation directly — the parent confirms first
// (see onUpdateField) — so this stays fully controlled by `value` (server data): a cancelled
// confirm just leaves the Select showing what it already showed, no revert logic needed.
const EditableSelectPill = ({ value, options, labels, onChange, ariaLabel, loading }) => (
  loading ? (
    <span className="inline-flex items-center gap-2 rounded-full border border-grey2 px-4 py-1.5 text-sm text-body whitespace-nowrap">
      <CircularProgress size={14} thickness={5} sx={{ color: '#12AFB8' }} />
      {labels[value] || value}
    </span>
  ) : (
    <Select
      value={value}
      onChange={(e) => onChange(e.target.value, labels[e.target.value] || e.target.value)}
      size="small"
      variant="outlined"
      inputProps={{ 'aria-label': ariaLabel }}
      sx={{
        borderRadius: '9999px',
        fontSize: '0.875rem',
        '& .MuiSelect-select': { padding: '8px 16px' },
      }}
    >
      {options.map((option) => (
        <MenuItem key={option} value={option}>
          {labels[option] || option}
        </MenuItem>
      ))}
    </Select>
  )
);

const Members = () => {
  const { ref, inView } = useInView();
  const { data: sessionData } = useValidateLogin();
  const [popup] = useContext(AlertContext);
  const removeMember = useRemoveMember();
  const resendInvite = useResendInvite();
  const cancelInvite = useCancelInvite();
  const updateMemberRoleAccess = useUpdateMemberRoleAccess();
  // Tracks which single member+field is mid-update so only that one pill shows a spinner — the
  // confirm popup (AlertContext) always resolves and closes itself the instant "Yes, Change" is
  // clicked, before the mutation below ever starts, so without this the request looked like it
  // silently vanished with no feedback while it was actually still in flight.
  const [pendingUpdate, setPendingUpdate] = useState(null);
  const [inviteModalOpen, setInviteModalOpen] = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const [searchKey, setSearchKey] = useState('');
  // Staged the same way CreditsUsage/UsageLedger.js's own FILTER panel stages its facets: the
  // panel edits a draft, and only APPLY FILTER promotes it into what the table actually uses.
  const [panelOpen, setPanelOpen] = useState(false);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [draft, setDraft] = useState(EMPTY_FILTERS);
  const panelRef = useRef(null);

  const callerPlatformRole = sessionData?.user?.platform_role;
  const callerAccessMode = sessionData?.user?.access_mode;
  const currentUserId = sessionData?.user?.id;
  const canManageMembers =
    MANAGE_ROLES.includes(callerPlatformRole) && callerAccessMode === accessModes.WRITE;
  // A customer_admin always operates within their own company (no selection needed); an
  // mssp_admin must have picked a target via the existing "Selected Customer" dropdown (AC15).
  const hasTargetCompany =
    callerPlatformRole === platformRoles.CUSTOMER_ADMIN || !!Storage.getStoredCompanyId();
  // ROAR-5006 — a company-tier viewer's roster can never contain MSSP staff rows (and vice
  // versa; see listMembersByCompany/listMembersByMssp's own tier split), so offering the other
  // tier's roles as filter checkboxes only ever yields an empty, confusing result. Reuses the
  // same family split memberMutationGate.js already enforces for role edits (getSameFamilyRoles)
  // rather than inventing a second tier-grouping rule. Falls back to every non-super_admin role
  // only for the brief window before the session resolves.
  const roleFilterOptions = callerPlatformRole
    ? getSameFamilyRoles(callerPlatformRole)
    : Object.keys(ROLE_LABELS).filter((role) => role !== platformRoles.SUPER_ADMIN);

  // Debounced the same way GlobalCompanySelection.js already debounces its own search input.
  useEffect(() => {
    const timeout = setTimeout(() => setSearchKey(searchInput.trim()), 500);
    return () => clearTimeout(timeout);
  }, [searchInput]);

  const {
    data,
    isLoading,
    hasNextPage,
    fetchNextPage,
  } = useMembers({ searchKey: searchKey || undefined });

  const onRemove = (member) => {
    popup({
      title: 'Are you sure?',
      description: `Removing ${member.name || member.email} will revoke their access.`,
      variant: 'warning',
      okayText: 'Yes, Remove',
      cancelText: 'Cancel',
    }).then(async () => {
      try {
        await removeMember.mutateAsync(member.id);
      } catch (err) {
        popup({
          variant: 'error',
          title: 'Could not remove member',
          description: err?.response?.data?.message || 'Something went wrong. Please try again.',
          okayText: 'Okay',
        });
      }
    });
  };

  // Resend is non-destructive (mints a fresh OTP/token, re-sends the same email) — no confirm
  // step, same as Invite Teammate itself.
  const onResendInvite = async (member) => {
    try {
      await resendInvite.mutateAsync(member.invite_id);
      popup({ variant: 'success', title: 'Invite resent', okayText: 'Okay' });
    } catch (err) {
      popup({
        variant: 'error',
        title: 'Could not resend invite',
        description: err?.response?.data?.message || 'Something went wrong. Please try again.',
        okayText: 'Okay',
      });
    }
  };

  const onCancelInvite = (member) => {
    popup({
      title: 'Are you sure?',
      description: `Cancelling the invite to ${member.name || member.email} means they can no longer accept it — you can invite them again later.`,
      variant: 'warning',
      okayText: 'Yes, Cancel Invite',
      cancelText: 'Keep Invite',
    }).then(async () => {
      try {
        await cancelInvite.mutateAsync(member.invite_id);
      } catch (err) {
        popup({
          variant: 'error',
          title: 'Could not cancel invite',
          description: err?.response?.data?.message || 'Something went wrong. Please try again.',
          okayText: 'Okay',
        });
      }
    });
  };

  // Confirms before applying (developer-confirmed) — the Select stays controlled by server data
  // (see EditableSelectPill's own comment), so a cancelled confirm needs no revert: nothing was
  // ever locally changed to begin with.
  const onUpdateField = (member, field, value, label) => {
    const fieldName = field === 'platform_role' ? 'role' : 'access';
    popup({
      title: 'Are you sure?',
      description: `Change ${member.name || member.email}'s ${fieldName} to ${label}?`,
      variant: 'warning',
      okayText: 'Yes, Change',
      cancelText: 'Cancel',
    }).then(async () => {
      setPendingUpdate({ memberId: member.id, field });
      try {
        await updateMemberRoleAccess.mutateAsync({ id: member.id, [field]: value });
      } catch (err) {
        popup({
          variant: 'error',
          title: 'Could not update member',
          description: err?.response?.data?.message || 'Something went wrong. Please try again.',
          okayText: 'Okay',
        });
      } finally {
        setPendingUpdate(null);
      }
    });
  };

  // Re-seeds the draft from what is actually applied on open — mirrors UsageLedger.js's own
  // togglePanel so dismissing without APPLY FILTER doesn't leave stale selections for next time.
  const togglePanel = () => {
    setPanelOpen((open) => {
      if (!open) setDraft(filters);
      return !open;
    });
  };

  // A click anywhere outside the panel dismisses it, same as UsageLedger.js's popover.
  useEffect(() => {
    if (!panelOpen) return undefined;
    const onPointerDown = (event) => {
      if (panelRef.current && !panelRef.current.contains(event.target)) {
        setPanelOpen(false);
      }
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [panelOpen]);

  const toggleDraftValue = (facet) => (value) =>
    setDraft((current) => ({
      ...current,
      [facet]: current[facet].includes(value)
        ? current[facet].filter((v) => v !== value)
        : [...current[facet], value],
    }));

  const isDraftDirty =
    draft.role.length !== filters.role.length ||
    draft.status.length !== filters.status.length ||
    draft.role.some((r) => !filters.role.includes(r)) ||
    draft.status.some((s) => !filters.status.includes(s));

  const applyFilters = () => {
    setFilters(draft);
    setPanelOpen(false);
  };

  const clearFilters = () => {
    setDraft(EMPTY_FILTERS);
    setFilters(EMPTY_FILTERS);
    setPanelOpen(false);
  };

  const activeFilterCount = filters.role.length + filters.status.length;

  const columns = useMemo(() => {
    const base = [
      {
        Header: 'NAME',
        accessor: 'name',
        Cell: ({ value, row }) => (
          <div>
            <div className="font-medium text-headings">{value || '-'}</div>
            <div className="text-xs text-[rgba(0,0,0,0.6)]">{row.original.email}</div>
          </div>
        ),
      },
      {
        Header: 'STATUS',
        accessor: 'displayStatus',
        Cell: ({ value }) => <StatusPill status={value} />,
      },
      {
        Header: 'ROLE',
        accessor: 'platform_role',
        Cell: ({ value, row }) => {
          const member = row.original;
          if (!canManageMembers || !hasTargetCompany || member.id === currentUserId) {
            return <TextPill>{resolveLabel(ROLE_LABELS, value)}</TextPill>;
          }
          return (
            <EditableSelectPill
              value={value}
              options={getSameFamilyRoles(value)}
              labels={ROLE_LABELS}
              ariaLabel={`Role for ${member.name || member.email}`}
              onChange={(next, label) => onUpdateField(member, 'platform_role', next, label)}
              loading={member.isUpdatingRole}
            />
          );
        },
      },
      {
        Header: 'ACCESS',
        accessor: 'access_mode',
        Cell: ({ value, row }) => {
          const member = row.original;
          if (!canManageMembers || !hasTargetCompany || member.id === currentUserId) {
            return <TextPill>{resolveLabel(ACCESS_LABELS, value)}</TextPill>;
          }
          return (
            <EditableSelectPill
              value={value}
              options={Object.values(accessModes)}
              labels={ACCESS_LABELS}
              ariaLabel={`Access for ${member.name || member.email}`}
              onChange={(next, label) => onUpdateField(member, 'access_mode', next, label)}
              loading={member.isUpdatingAccess}
            />
          );
        },
      },
    ];
    if (!canManageMembers || !hasTargetCompany) return base;
    return [
      ...base,
      {
        Header: 'ACTIONS',
        accessor: 'actions',
        className: 'text-right',
        Cell: ({ value: member }) => {
          if (member.id === currentUserId) return null;
          // A pending invite (invite_id non-null — see the module comment's IMPORTANT note) gets
          // Re-Invite/Cancel too; Edit role & access is gone from this menu entirely now that
          // ROLE/ACCESS are live dropdowns in their own columns.
          const options =
            member.invite_id != null
              ? [
                  {
                    value: 'resend',
                    label: 'Re-Invite User',
                    // MuiMenu.js's default (unset labelClass) is text-subtitle, which is the
                    // same rgba(0,0,0,0.3) token this app's own --disabled color reuses — reads
                    // as a disabled item even though it's fully clickable. Explicit normal text
                    // color here, same reasoning as Remove member's own explicit (red) labelClass.
                    labelClass: 'text-headings',
                    click: () => onResendInvite(member),
                  },
                  {
                    value: 'cancel',
                    label: 'Cancel Invite',
                    labelClass: 'text-headings',
                    click: () => onCancelInvite(member),
                  },
                ]
              : [];
          options.push({
            value: 'remove',
            label: 'Remove member',
            labelClass: 'text-functional-red',
            click: () => onRemove(member),
          });
          // No icons on this pane's options (unlike AssetList/AllSchedules) — MuiMenu.js only
          // reserves the icon-button slot when option.icon is actually set, so these render as
          // plain text rows. Narrower width since the labels here are short.
          return <LongMenu options={options} menuClassName="!w-[190px]" />;
        },
      },
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canManageMembers, hasTargetCompany, currentUserId]);

  // Flatten every page — the table renders the whole accumulated list, not just the latest page.
  // Guards a page payload without `items`, which is what a failed or empty read looks like.
  const allRows = useMemo(
    () =>
      (data?.pages || []).flatMap((page) =>
        (page?.items || []).map((member) => {
          const name = `${member.first_name || ''} ${member.last_name || ''}`.trim();
          const displayStatus = getDisplayStatus(member);
          return { ...member, name, displayStatus, actions: { ...member, name, displayStatus } };
        })
      ),
    [data]
  );

  // Client-side only — see the module comment above for why this can't reach unloaded pages.
  // isUpdatingRole/isUpdatingAccess ride along on each row's own data (not just a `columns`
  // closure) so the live dropdown's loading spinner reaches react-table's actual rendered rows
  // reliably — react-table only reliably reacts to `data` changing, not to `columns`' own cell
  // closures going stale between recomputations of the same column set.
  const rows = useMemo(
    () =>
      allRows
        .filter((member) => {
          if (filters.role.length && !filters.role.includes(member.platform_role)) return false;
          if (filters.status.length && !filters.status.includes(member.displayStatus)) return false;
          return true;
        })
        .map((member) => ({
          ...member,
          isUpdatingRole: pendingUpdate?.memberId === member.id && pendingUpdate?.field === 'platform_role',
          isUpdatingAccess: pendingUpdate?.memberId === member.id && pendingUpdate?.field === 'access_mode',
        })),
    [allRows, filters, pendingUpdate]
  );

  useEffect(() => {
    if (inView && hasNextPage) fetchNextPage();
  }, [inView, hasNextPage, fetchNextPage]);

  return (
    <div>
      <div className="flex items-center justify-between">
        <div>
          <Typography variant="h5" className="font-semibold">
            Team Members
          </Typography>
          <Typography variant="body2" className="text-[rgba(0,0,0,0.6)] mt-1">
            {rows.length}
            {hasNextPage ? '+' : ''} members &middot; invite-only for external accounts
          </Typography>
        </div>
        {canManageMembers && (
          <Button type="button" size="small" onClick={() => setInviteModalOpen(true)}>
            Invite Teammate
          </Button>
        )}
      </div>
      {canManageMembers && !hasTargetCompany && (
        <Typography variant="body2" className="text-functional-red mt-2">
          Select a customer from the dropdown above to invite, edit, or remove members.
        </Typography>
      )}

      {/* Search box + FILTER trigger/panel mirror CreditsUsage/UsageLedger.js's own markup/classes
          (bg-primary-50 trigger, staged draft + APPLY FILTER/CLEAR ALL) rather than inventing a
          second filter convention for the same Settings modal — icon is the filled funnel
          (FilterAltIcon) per the developer-provided mock, not UsageLedger's own TuneIcon. */}
      <div className="mt-4 flex items-center gap-3">
        <div className="flex grow items-center gap-2 rounded border border-grey3 px-3 py-2">
          <SearchIcon fontSize="small" className="text-body" />
          <input
            className="w-full outline-none"
            placeholder="Search member or role or access"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          />
        </div>

        <div className="relative shrink-0" ref={panelRef}>
          <button
            type="button"
            className="flex items-center gap-2 rounded bg-primary-50 px-4 py-2 text-primary-700"
            onClick={togglePanel}
          >
            <FilterAltIcon fontSize="small" />
            FILTER{activeFilterCount ? ` (${activeFilterCount})` : ''}
          </button>

          {panelOpen && (
            <div className="absolute right-0 top-full z-20 mt-2 w-[300px] rounded-lg bg-white p-4 shadow-lg ring-1 ring-grey3">
              <div className="flex flex-col gap-4">
                <div>
                  <Typography variant="subtitle2" className="font-semibold mb-2">
                    Role
                  </Typography>
                  {roleFilterOptions.map((role) => (
                    <FormControlLabel
                      key={role}
                      className="block"
                      control={
                        <Checkbox
                          size="small"
                          checked={draft.role.includes(role)}
                          onChange={() => toggleDraftValue('role')(role)}
                        />
                      }
                      label={ROLE_LABELS[role]}
                    />
                  ))}
                </div>
                <div>
                  <Typography variant="subtitle2" className="font-semibold mb-2">
                    Status
                  </Typography>
                  {STATUS_FILTER_OPTIONS.map((status) => (
                    <FormControlLabel
                      key={status}
                      className="block"
                      control={
                        <Checkbox
                          size="small"
                          checked={draft.status.includes(status)}
                          onChange={() => toggleDraftValue('status')(status)}
                        />
                      }
                      label={STATUS_LABELS[status]}
                    />
                  ))}
                </div>
              </div>

              <div className="mt-4 flex items-center justify-between gap-3 border-t border-[#DADADA] pt-4">
                <button
                  type="button"
                  className="rounded border border-primary-500 px-4 py-2 text-sm font-medium text-primary-500"
                  onClick={clearFilters}
                >
                  CLEAR ALL
                </button>
                <button
                  type="button"
                  disabled={!isDraftDirty}
                  className={`rounded px-4 py-2 text-sm font-medium text-white ${
                    isDraftDirty ? 'bg-primary-500' : 'cursor-not-allowed bg-[#0000004D]'
                  }`}
                  onClick={applyFilters}
                >
                  APPLY FILTER
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {isLoading ? (
        <TableSkeleton rowsNum={5} />
      ) : (
        <>
          <div className={`mt-4 ${rows.length === 0 ? 'settings-table-frame' : ''}`}>
            <ReactTable
              data={rows}
              columns={columns}
              dependencyArray={[rows, columns]}
              className="table settings-table w-full"
              headerClassName="background rounded [&_th]:text-[10px] [&_th]:!font-bold"
              bodyClassName="text-headings"
              emptyDataText="No members yet"
            />
          </div>
          {hasNextPage && (
            <div data-testid="members-sentinel" className="h-px" ref={ref} />
          )}
        </>
      )}

      <Typography variant="body2" className="text-[rgba(0,0,0,0.6)] mt-4">
        *Read only members can view but cannot modify anything in the account.
      </Typography>

      <InviteTeammateModal
        open={inviteModalOpen}
        onClose={() => setInviteModalOpen(false)}
        callerPlatformRole={callerPlatformRole}
        hasTargetCompany={hasTargetCompany}
      />
    </div>
  );
};

export default Members;
